import React from 'react';
import { SchematicShell, Rect, Line } from './SchematicPrimitives';
import { sofaProportions } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for `sofa` — the Track-Arm 3-seater.
 *
 * Matches `sofaRenderer` in furnitureRenderers.js (shared proportions live in
 * `proceduralRenderers/proceduralSpecs.js` → `sofaProportions`): a long low
 * tailored body on thin matte-black metal pin legs, with two thin squared track
 * arms framing a shallow plinth, a tight low back, and three crisp seat + back
 * cushions.
 *
 * Front: two squared arms, a low back panel, the plinth between the arms, three
 * seat + three back cushions, two front pin legs.
 * Side: the full-depth arm slab, a low back hint at the rear, a seat-cushion
 * line, and front/back pin legs. Right view mirrors left.
 *
 * All numbers come from `sofaProportions` (floor-relative, floor at the bottom
 * of the viewport), so the 2D elevation can never drift from the 3D geometry.
 */

export function SofaSchematic({
  obj,
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  // Same `attributes.SofaType` the 3D renderer and the 2D floor plan read, so
  // the elevation shows the seat count the object actually has. `obj` is the
  // schematic registry's standard channel for object fields (see
  // StorageSchematic).
  const s = sofaProportions(
    { width, height, depth },
    obj?.attributes?.SofaType
  );

  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        drawFrame={false}
      >
        <g
          transform={
            view === 'right' ? `scale(-1, 1) translate(${-depth}, 0)` : ''
          }
        >
          <SideProfile
            depth={depth}
            height={height}
            s={s}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT — svg y grows downward; floor is at y = height.
  const yTop = (fromFloor) => height - fromFloor;
  const legCx = [s.legInset, width - s.legInset];
  const legW = 2 * s.legR;
  const backW = s.innerW + 0.01;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Tight low back panel between the arm tops. */}
      <Rect
        x={width / 2 - backW / 2}
        y={yTop(s.topY)}
        width={backW}
        height={s.backH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Two squared track arms (full height to the shared top). */}
      <Rect
        x={0}
        y={yTop(s.topY)}
        width={s.armW}
        height={s.armH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={width - s.armW}
        y={yTop(s.topY)}
        width={s.armW}
        height={s.armH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Shallow upholstered plinth between the arms. */}
      <Rect
        x={width / 2 - s.baseW / 2}
        y={yTop(s.baseTop)}
        width={s.baseW}
        height={s.baseH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Three back cushions. */}
      {Array.from({ length: s.seatCount }, (_, i) => {
        const cx = width / 2 + (-s.areaW / 2 + s.cushW * (i + 0.5));
        const w = s.cushW - 0.022;
        return (
          <Rect
            key={`back-${i}`}
            x={cx - w / 2}
            y={yTop(s.backCushBottom + s.backCushH)}
            width={w}
            height={s.backCushH}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
      {/* Three seat cushions on the plinth. */}
      {Array.from({ length: s.seatCount }, (_, i) => {
        const cx = width / 2 + (-s.areaW / 2 + s.cushW * (i + 0.5));
        const w = s.cushW - 0.018;
        return (
          <Rect
            key={`seat-${i}`}
            x={cx - w / 2}
            y={yTop(s.baseTop + s.seatCushionH - 0.006)}
            width={w}
            height={s.seatCushionH}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
      {/* Two front pin legs. */}
      {legCx.map((cx, i) => (
        <Rect
          key={`leg-${i}`}
          x={cx - s.legR}
          y={yTop(s.legH)}
          width={legW}
          height={s.legH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </SchematicShell>
  );
}

function SideProfile({ depth, height, s, strokeColor, strokeWidth }) {
  // Side view: viewport width = depth. Renderer z maps to x via z + depth/2,
  // so the back of the sofa (-z) is on the left, the front (+z) on the right.
  const yTop = (fromFloor) => height - fromFloor;
  const armX = depth / 2 - s.armD / 2;
  const legCx = [s.legInset, depth - s.legInset];
  const legW = 2 * s.legR;

  return (
    <g>
      {/* Full-depth arm slab — the dominant side silhouette. */}
      <Rect
        x={armX}
        y={yTop(s.topY)}
        width={s.armD}
        height={s.armH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Seat-cushion break line across the front face. */}
      <Line
        x1={armX}
        y1={yTop(s.baseTop + s.seatCushionH)}
        x2={armX + s.armD}
        y2={yTop(s.baseTop + s.seatCushionH)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Low back hint at the rear (left) edge. */}
      <Line
        x1={depth / 2 + s.backZ}
        y1={yTop(s.topY)}
        x2={depth / 2 + s.backZ}
        y2={yTop(s.baseTop)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Front + back pin legs. */}
      {legCx.map((cx, i) => (
        <Rect
          key={`leg-${i}`}
          x={cx - s.legR}
          y={yTop(s.legH)}
          width={legW}
          height={s.legH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </g>
  );
}

SofaSchematic.supportsSideViews = true;
